import { NSARouterMakerResult } from 'namirasoft-account-react';
import { EntryLineInputRow, TemplateGenerator, TemplateRow, UserFullRow } from 'namirasoft-password';
import { NamirasoftMap } from 'namirasoft-site-map';
import { IBaseComponentProps, NSBox, NSBoxEntity, NSBoxSensitive, NSButton, NSButtonBlue, NSColumn, NSDialog, NSDialogBoxString, NSMenuButtonMenuItemProps } from 'namirasoft-site-react';
import { Component, createRef } from 'react';
import { PasswordRouterProps } from '../PasswordRouterProps';
import { DefaultTemplates } from '../DefaultTemplates';
import { NSBoxPassword } from './NSBoxPassword';

export interface EntryLineProps extends IBaseComponentProps, NSARouterMakerResult, PasswordRouterProps
{
    user: UserFullRow;
    index: number;
    deletable: boolean;
    onDelete: () => void;
    onChanged?: (value: any) => void;
    onSensitiveChange?: (sensitive: boolean) => void;
}

export interface EntryLineState
{
    title: string;
    isSensitive: boolean;
    generated_password: string | null;
    show_edit_title_dialog: boolean;
    show_generate_dialog: boolean;
}

export class EntryLine extends Component<EntryLineProps, EntryLineState>
{
    static IS_DIRTY(row: EntryLineInputRow | null): boolean
    {
        return Boolean(row?.value);
    }

    private NSBoxPassword_EntryLine = createRef<NSBoxPassword>();
    private NSBoxEntity_Template = createRef<NSBoxEntity<TemplateRow>>();
    private NSBoxSensitive_GeneratedPassword = createRef<NSBoxSensitive>();

    constructor(props: EntryLineProps)
    {
        super(props);
        this.state = {
            show_edit_title_dialog: false,
            show_generate_dialog: false,
            generated_password: null,
            title: "Entry Line",
            isSensitive: false,
        }
        this.getValue = this.getValue.bind(this);
        this.setValue = this.setValue.bind(this);
        this.isDirty = this.isDirty.bind(this);
        this.onChanged = this.onChanged.bind(this);
        this.openGenerateDialog = this.openGenerateDialog.bind(this);
        this.onTemplateLoaded = this.onTemplateLoaded.bind(this);
        this.generate = this.generate.bind(this);
        this.applyGenerated = this.applyGenerated.bind(this);
    }

    private onTemplateLoaded()
    {
        let combo = this.NSBoxEntity_Template.current?.NSBoxBaseCombo.current;
        if (!combo || combo.getDataOne(false))
            return;
        let rows = combo.getRows();
        if (rows.length === 0)
            return;
        combo.setDataOne(rows[0], this.generate);
    }

    private openGenerateDialog()
    {
        if (!this.NSBoxPassword_EntryLine.current?.isSensitive())
        {
            this.props.notifier.onError("Only sensitive fields can be generated from a Template.");
            return;
        }
        this.setState(() => ({ show_generate_dialog: true, generated_password: null }));
    }

    private generate()
    {
        try
        {
            let template = this.NSBoxEntity_Template.current?.getDataOne();
            if (!template)
            {
                this.props.notifier.onError("Please select a Template first.");
                return;
            }
            let generated_password = TemplateGenerator.generate(template);
            this.setState(
                () => ({ generated_password }),
                () => this.NSBoxSensitive_GeneratedPassword.current?.setValue(generated_password),
            );
        }
        catch (error)
        {
            this.props.notifier.onError(error as string);
        }
    }

    private applyGenerated()
    {
        if (!this.state.generated_password)
            return;
        this.NSBoxPassword_EntryLine.current?.setValue(this.state.generated_password, this.onChanged);
        this.setState(() => ({ show_generate_dialog: false, generated_password: null }));
    }

    getValue(checkError?: boolean): EntryLineInputRow
    {
        let name = this.state.title;
        let sensitive = this.NSBoxPassword_EntryLine.current?.isSensitive() ?? false;
        let value = this.NSBoxPassword_EntryLine.current?.getValue(checkError) ?? "";

        return { name, sensitive, value };
    }

    async getEncryptedValue(checkError?: boolean): Promise<EntryLineInputRow>
    {
        let name = this.state.title;
        let sensitive = this.NSBoxPassword_EntryLine.current?.isSensitive() ?? false;
        let value = await this.NSBoxPassword_EntryLine.current?.getEncryptedValue(checkError) ?? "";

        return { name, sensitive, value };
    }

    setValue(row: EntryLineInputRow)
    {
        this.setState(() => ({ title: row.name }))
        this.NSBoxPassword_EntryLine.current?.setSensitive(row.sensitive, () =>
        {
            this.NSBoxPassword_EntryLine.current?.setValue(row.value);
        });
    }

    setEncryptedValue(row: EntryLineInputRow)
    {
        this.setState(() => ({ title: row.name }));
        this.NSBoxPassword_EntryLine.current?.setSensitive(row.sensitive, () =>
        {
            this.NSBoxPassword_EntryLine.current?.setValue(row.value);
        });
    }

    isDirty(): boolean
    {
        return EntryLine.IS_DIRTY(this.getValue(false));
    }

    private onChanged()
    {
        if (this.props.onChanged)
            this.props.onChanged(this.getValue(false));
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        let menu_items: NSMenuButtonMenuItemProps[] = [
            {
                title: "Edit",
                icon: "https://static.namirasoft.com/image/concept/edit/blue.svg",
                onClick: () => this.setState(() => ({ show_edit_title_dialog: true })),
            }
        ];
        if (this.state.isSensitive)
        {
            menu_items.push({
                title: "Generate Password",
                icon: "https://static.namirasoft.com/image/concept/magic-wand/blue.svg",
                onClick: () => this.setState(() => ({ show_generate_dialog: true }))
            })
        }

        return (
            <>
                <NSBoxPassword
                    {...this.props}
                    ref={this.NSBoxPassword_EntryLine}
                    title={this.state.title}
                    required
                    style={{ width: "100%" }}
                    auto_complete="new-password"
                    onChanged={this.onChanged}
                    menu={{
                        builtin: {
                            delete: {
                                enabled: this.props.deletable,
                                onDelete: this.props.onDelete
                            }
                        },
                        items: menu_items,
                    }}
                    onSensitiveChange={(sensitive) => this.setState(() => ({ isSensitive: sensitive }))}
                    info={{ link: "https://namirasoft.com/password/console-guide/entry/#field-entry-line" }}
                />
                {
                    this.state.show_edit_title_dialog &&
                    <NSDialogBoxString
                        box={{
                            title: "Entry Line Name",
                            defaultValue: this.state.title,
                            required: true,
                            style: { width: NSBox.width.double, marginTop: "1rem" }
                        }}
                        button={{
                            title: "Apply",
                            onClick: {
                                showLoading: false,
                                action: (_onFinished, box) => this.setState(() => ({ show_edit_title_dialog: false, title: box?.getValue() ?? "" }))
                            }
                        }}
                        onClose={() => this.setState(() => ({ show_edit_title_dialog: false }))}
                    >
                        <></>
                    </NSDialogBoxString>
                }
                {
                    this.state.show_generate_dialog &&
                    <NSDialog
                        center_dialog={true}
                        onClose={() => this.setState(() => ({ show_generate_dialog: false, generated_password: null }))}
                    >
                        <NSColumn classList={["align-items-center"]}>
                            <NSBoxEntity<TemplateRow>
                                ref={this.NSBoxEntity_Template}
                                title="Template"
                                table={namirasoft.password.tables.template}
                                list={async (filters, page, size, sorts) =>
                                {
                                    let result = await this.props.servers.password.template.List(filters, page, size, sorts, null);
                                    if (result.count === 0)
                                        return { count: DefaultTemplates.length, rows: DefaultTemplates };
                                    return result;
                                }}
                                required={false}
                                multiple={false}
                                getValue={(item) => item.id}
                                getTitle={(item) => item.name}
                                getSort={() => null}
                                onLoaded={this.onTemplateLoaded}
                                onChanged={this.generate}
                            />
                            {
                                this.state.generated_password &&
                                <NSBoxSensitive
                                    ref={this.NSBoxSensitive_GeneratedPassword}
                                    title="Password"
                                    required={false}
                                    auto_complete="new-password"
                                    menu={{
                                        items: [],
                                        builtin: {
                                            copy: {
                                                enabled: true,
                                                getValue: () => this.state.generated_password ?? ""
                                            }
                                        },
                                    }}
                                />
                            }
                            <NSColumn classList={["align-items-center gap-0"]}>
                                <NSButtonBlue
                                    title="Apply"
                                    onClick={{ showLoading: false, action: this.applyGenerated }}
                                />
                                <NSButton
                                    title="Regenerate"
                                    onClick={{ showLoading: false, action: this.generate }}
                                />
                            </NSColumn>
                        </NSColumn>
                    </NSDialog>
                }
            </>
        )
    }
}

export default EntryLine